
import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import LanguageSwitcher from '../LanguageSwitcher';
import { useNavigationUtils } from '@/hooks/useNavigationUtils';

interface MobileNavigationProps {
  isMenuOpen: boolean;
  setIsMenuOpen: (isOpen: boolean) => void;
  isServicesActive: boolean;
}

const MobileNavigation: React.FC<MobileNavigationProps> = ({ 
  isMenuOpen, 
  setIsMenuOpen,
  isServicesActive 
}) => {
  const { currentLanguage, getLocalizedPath } = useLanguage();
  const t = translations[currentLanguage];
  const { 
    isWhyFutureLevelActive, 
    getWhyFutureLevelPath, 
    isProductVideosActive, 
    getProductVideosPath,
    isSocialMediaAdsActive,
    getSocialMediaAdsPath,
    isPricingActive,
    getPricingPath,
    isAIVideoActive,
    getAIVideoPath,
    isWebdesignActive,
    getWebdesignPath,
    isSEOActive,
    getSEOPath,
    isGoogleBusinessProfileActive,
    getGoogleBusinessProfilePath
  } = useNavigationUtils();

  return (
    <div 
      id="mobile-navigation"
      className={`md:hidden bg-gradient-to-b from-[#1A1F2C] to-[#141722] shadow-2xl shadow-purple-900/30 border-t border-purple-900/30 overflow-y-auto transition-all duration-500 ease-in-out z-40 ${
        isMenuOpen ? 'max-h-[80vh] opacity-100' : 'max-h-0 opacity-0'
      }`}
      aria-hidden={!isMenuOpen}
    >
      <div className="container mx-auto px-6 py-6">
        {/* Language switcher at the top for better visibility */}
        <div className="flex justify-center mb-6">
          <LanguageSwitcher />
        </div>
        
        <nav className="flex flex-col space-y-4 pb-6">
          <Link 
            to={`/${currentLanguage}`}
            className="text-gray-100 hover:text-purple-400 transition-colors font-future tracking-wider text-base relative group py-2"
            onClick={() => setIsMenuOpen(false)}
          >
            <span className="uppercase font-medium">{t.home}</span>
            <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-gradient-to-r from-purple-500 to-pink-500 group-hover:w-[30%] transition-all duration-300"></span>
          </Link>
          
          {/* Services Section */}
          <div className="space-y-3">
            <div className="text-purple-300 text-xs font-medium uppercase tracking-wider px-2 py-1">Services</div>
            
            <Link 
              to={getAIVideoPath()}
              className={`text-gray-200 hover:text-purple-400 transition-colors text-sm pl-4 py-1 block ${
                isAIVideoActive() ? 'text-purple-400' : ''
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              AI-Videos
            </Link>
            
            <Link 
              to={getWebdesignPath()}
              className={`text-gray-200 hover:text-purple-400 transition-colors text-sm pl-4 py-1 block ${
                isWebdesignActive() ? 'text-purple-400' : ''
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              Webdesign
            </Link>
            
            <Link 
              to={getSEOPath()}
              className={`text-gray-200 hover:text-purple-400 transition-colors text-sm pl-4 py-1 block ${
                isSEOActive() ? 'text-purple-400' : ''
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              SEO
            </Link>
            
            <Link 
              to={getGoogleBusinessProfilePath()}
              className={`text-gray-200 hover:text-purple-400 transition-colors text-sm pl-4 py-1 block ${
                isGoogleBusinessProfileActive() ? 'text-purple-400' : ''
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              Google Business
            </Link>
            
            <div className="border-t border-gray-700 my-2 mx-4"></div>
            
            <Link 
              to={getProductVideosPath()}
              className={`text-gray-200 hover:text-purple-400 transition-colors text-sm pl-4 py-1 block ${
                isProductVideosActive() ? 'text-purple-400' : ''
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              Produktvideos
            </Link>
            
            <Link 
              to={getSocialMediaAdsPath()}
              className={`text-gray-200 hover:text-purple-400 transition-colors text-sm pl-4 py-1 block ${
                isSocialMediaAdsActive() ? 'text-purple-400' : ''
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              Social Media Ads
            </Link>
            
            <div className="border-t border-gray-700 my-3 mx-4"></div>
            <div className="text-purple-300 text-xs font-medium uppercase tracking-wider px-2 py-1">International</div>
            
            <div className="pl-4 space-y-2">
              <div className="text-xs text-gray-400 font-medium">Germany (DE):</div>
              <div className="pl-2 space-y-1">
                <Link 
                  to="/en/stuttgart" 
                  className="text-gray-200 hover:text-purple-400 transition-colors text-xs block py-1"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Stuttgart
                </Link>
                <Link 
                  to="/en/munich" 
                  className="text-gray-200 hover:text-purple-400 transition-colors text-xs block py-1"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Munich
                </Link>
              </div>
              
              <div className="text-xs text-gray-400 font-medium mt-2">France (FR):</div>
              <div className="pl-2 space-y-1">
                <Link 
                  to="/en/lyon" 
                  className="text-gray-200 hover:text-purple-400 transition-colors text-xs block py-1"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Lyon
                </Link>
                <Link 
                  to="/en/paris" 
                  className="text-gray-200 hover:text-purple-400 transition-colors text-xs block py-1"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Paris
                </Link>
              </div>
              
              <div className="text-xs text-gray-400 font-medium mt-2">Italy (IT):</div>
              <div className="pl-2 space-y-1">
                <Link 
                  to="/en/milan" 
                  className="text-gray-200 hover:text-purple-400 transition-colors text-xs block py-1"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Milan
                </Link>
                <Link 
                  to="/en/como" 
                  className="text-gray-200 hover:text-purple-400 transition-colors text-xs block py-1"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Como
                </Link>
              </div>
            </div>
          </div>
          
          <Link 
            to={getPricingPath()}
            className={`text-gray-100 hover:text-purple-400 transition-colors font-future tracking-wider text-base relative group py-2 ${
              isPricingActive() ? 'text-purple-400' : ''
            }`}
            onClick={() => setIsMenuOpen(false)}
          >
            <span className="uppercase font-medium">{t.pricing}</span>
            <span className={`absolute -bottom-1 left-0 h-[2px] bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300 ${
              isPricingActive() ? 'w-[30%]' : 'w-0 group-hover:w-[30%]'
            }`}></span>
          </Link>
          
          <Link 
            to={getWhyFutureLevelPath()}
            className={`text-gray-100 hover:text-purple-400 transition-colors font-future tracking-wider text-base relative group py-2 ${
              isWhyFutureLevelActive() ? 'text-purple-400' : ''
            }`}
            onClick={() => setIsMenuOpen(false)}
          >
            <span className="uppercase font-medium">{t.whyFutureLevel}</span>
            <span className={`absolute -bottom-1 left-0 h-[2px] bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300 ${
              isWhyFutureLevelActive() ? 'w-[30%]' : 'w-0 group-hover:w-[30%]'
            }`}></span>
          </Link>
          
          <Link 
            to={getLocalizedPath('blog')}
            className="text-gray-100 hover:text-purple-400 transition-colors font-future tracking-wider text-base relative group py-2"
            onClick={() => setIsMenuOpen(false)}
          >
            <span className="uppercase font-medium">{t.blog}</span>
            <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-gradient-to-r from-purple-500 to-pink-500 group-hover:w-[30%] transition-all duration-300"></span>
          </Link>
          
          <Link 
            to={getLocalizedPath('contact')}
            className="text-gray-100 hover:text-purple-400 transition-colors font-future tracking-wider text-base relative group py-2"
            onClick={() => setIsMenuOpen(false)}
          >
            <span className="uppercase font-medium">{t.contact}</span>
            <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-gradient-to-r from-purple-500 to-pink-500 group-hover:w-[30%] transition-all duration-300"></span>
          </Link>
        </nav>
      </div>
    </div>
  );
};

export default MobileNavigation;
